import { useEffect, useState } from 'react';
import type { Locale } from '@superposition/domain';
import * as api from '../../lib/api.js';
import { haptic } from '../../lib/telegram.js';
import { setProfile, useLocale } from '../../lib/profile.js';
import { useT } from '../../i18n/index.js';

type Stage = { kind: 'loading' } | { kind: 'ready' } | { kind: 'saving' } | { kind: 'error'; message: string };

/** имя и время ежедневного вопроса: то же, что бот спрашивает при знакомстве */
export function SettingsSheet({ onClose }: { onClose: () => void }): React.ReactNode {
  const t = useT();
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  const [name, setName] = useState('');
  const [remindAt, setRemindAt] = useState('21:00');
  const [remindersOn, setRemindersOn] = useState(true);
  const [gender, setGender] = useState<'m' | 'f' | 'n' | null>(null);
  const [saved, setSaved] = useState(false);
  const [locales, setLocales] = useState<Locale[]>([]);
  const locale = useLocale();

  useEffect(() => {
    let alive = true;
    api
      .fetchProfile()
      .then((p) => {
        if (!alive) return;
        setName(p.name ?? '');
        setRemindAt(p.remindAt ?? '21:00');
        // время еще не выбрано — по умолчанию вопрос включен на 21:00
        setRemindersOn(p.remindersOn);
        setGender(p.gender);
        setLocales(p.locales ?? []);
        setStage({ kind: 'ready' });
      })
      .catch((err: unknown) => alive && setStage({ kind: 'error', message: api.describeFailure(err, t) }));
    return () => {
      alive = false;
    };
  }, []);

  const save = async (): Promise<void> => {
    haptic('medium');
    setStage({ kind: 'saving' });
    try {
      const saved = await api.saveProfile({
        name: name.trim() || null,
        remindAt: remindersOn ? remindAt : null,
        remindersOn,
        gender,
      });
      setProfile(saved);
      setSaved(true);
      setStage({ kind: 'ready' });
    } catch (err) {
      setStage({ kind: 'error', message: api.describeFailure(err, t) });
    }
  };

  // язык меняется сразу, без кнопки «сохранить»: весь интерфейс перерисуется
  const changeLocale = async (l: Locale): Promise<void> => {
    if (l === locale) return;
    haptic('select');
    setStage({ kind: 'saving' });
    try {
      const next = await api.saveLocale(l);
      setProfile(next);
      setLocales(next.locales ?? []);
      setStage({ kind: 'ready' });
    } catch (err) {
      setStage({ kind: 'error', message: api.describeFailure(err, t) });
    }
  };

  const languageNames: Record<Locale, string> = { ru: t.settings.languageRu, en: t.settings.languageEn };
  const busy = stage.kind === 'loading' || stage.kind === 'saving';

  return (
    <div
      className="sheet sheet-top"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sheet-in settings">
        <h3 id="settings-title">{t.settings.title}</h3>

        <label className="set-field">
          <span>{t.settings.name}</span>
          <input
            value={name}
            maxLength={40}
            autoComplete="given-name"
            placeholder={t.settings.namePlaceholder}
            disabled={busy}
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
            }}
          />
        </label>

        <div className="set-field" role="group" aria-labelledby="gender-label">
          <span id="gender-label">{t.settings.genderLabel}</span>
          <div className="set-seg">
            {(
              [
                ['m', t.settings.genderM],
                ['f', t.settings.genderF],
                ['n', t.settings.genderN],
              ] as const
            ).map(([g, label]) => (
              <button
                type="button"
                key={g}
                className={gender === g ? 'sel' : ''}
                aria-pressed={gender === g}
                disabled={busy}
                onClick={() => {
                  setGender(g);
                  setSaved(false);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {locales.length > 1 ? (
          <div className="set-field" role="group" aria-labelledby="locale-label">
            <span id="locale-label">{t.settings.language}</span>
            <div className="set-seg">
              {locales.map((l) => (
                <button
                  type="button"
                  key={l}
                  lang={l}
                  className={locale === l ? 'sel' : ''}
                  aria-pressed={locale === l}
                  disabled={busy}
                  onClick={() => void changeLocale(l)}
                >
                  {languageNames[l]}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <label className="set-toggle">
          <input
            type="checkbox"
            checked={remindersOn}
            disabled={busy}
            onChange={(e) => {
              setRemindersOn(e.target.checked);
              setSaved(false);
            }}
          />
          <span>{t.settings.remindersOn}</span>
        </label>

        {remindersOn ? (
          <label className="set-field">
            <span>{t.settings.remindAt}</span>
            <input
              type="time"
              value={remindAt}
              disabled={busy}
              onChange={(e) => {
                setRemindAt(e.target.value || '21:00');
                setSaved(false);
              }}
            />
          </label>
        ) : null}

        {stage.kind === 'error' ? <p className="set-error">{stage.message}</p> : null}
        {saved ? <p className="set-ok">{t.common.saved}</p> : null}

        <div className="sheet-actions">
          <button type="button" onClick={onClose}>
            {t.common.close}
          </button>
          <button type="button" className="primary" disabled={busy} onClick={() => void save()}>
            {stage.kind === 'saving' ? t.common.saving : t.common.save}
          </button>
        </div>
      </div>
    </div>
  );
}
